import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
    Box, Button,
    Fade, Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Modal,
    Popover,
    Stack,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import React, { useMemo, useRef, useState } from 'react';
import { ReactComponent as DeleteIcon } from "../assets/icons/icon-delete.svg";
import { ReactComponent as HashtagIcon } from "../assets/icons/icon-hashtag.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/icon_close.svg";
import { ReactComponent as EditIcon } from "../assets/icons/icon_edit.svg";
import DashboardGranularity from '../components/_report/dashboard/DashboardGranularity';
import FormatControl from '../components/_reportDashboardFilter/FormatControl';
import ReportDashboardFilterForm from '../components/_reportDashboardFilter/ReportDashboardFilterForm';
import { CustomTooltip } from '../utils/Reusables';
import { useDashboardContext } from '../layouts/dashboardView/DashboardView';
import GlobalDateRange from '../components/_report/dashboard/GlobalDateRange';
import toast from 'react-hot-toast';

const RootStyle = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
    },
}));

const useStyles = makeStyles((theme) => ({
    bgWhite: {
        backgroundColor: '#fff',
        borderRadius: '4px',
    },
    formInput: {
        paddingLeft: 0,
        '&::placeholder': {
            paddingLeft: '-10px',
        }
    },
    searchFieldListItem: {
        paddingTop: '0.6rem',
        paddingBottom: '0.6rem',
        '&:hover': {
            background: "#f1f1f1",
            backgroundColor: "#f1f1f1",
            zIndex: 9,
            "& $listItemDescription": {
                visibility: 'visible'
            },
            "& $searchFieldListMoreIcon": {
                visibility: 'visible'
            }
        },

    },
    listItemDescription: {
        visibility: 'visible',
        '&:hover': {
            visibility: 'visible'
        }
    },
    searchFieldListMoreIcon: {
        visibility: 'visible',
        '&:hover': {
            visibility: 'visible'
        }
    }
}));

export default function ReportDashboardFilter({
    filterFormState,
    setFilterFormState,
    valueList,
    granularityOptions,
    addGranularityToSheet,
    sheetsAvailable,
    setSheetsAvailable,
    selectedSheetIndex,
    applyGranularityToAllReports,
    removeGranularityFrmAllReports
}) {

    const { currentSheet, updateSheet, dashboardState, updateSheetFilter } = useDashboardContext();
    const filterOptionList = useMemo(() => {
        if (!currentSheet || currentSheet.reports.length === 0) return [];
        return currentSheet.reports.map(report => {
            return {
                report,
                filterOptionList: report?.reportFilters?.filter(filter => filter?._id && filter?.listType)
            }
        });
    }, [currentSheet])

    const [anchorAddFilter, setAnchorAddFilter] = useState(null);
    const [filterFormOpen, setFilterFormFilter] = useState(-1);
    const [openPopup, setOpenPopup] = useState(false);
    const [recordForEdit, setRecordForEdit] = useState(null)
    const [deletePopup, setDeletePopup] = useState(false)
    const [deleteAllPopup, setDeleteAllPopup] = useState(false)
    const [filterButtonWidth, setFilterButtonWidth] = useState('200px')
    const [deleteObj, setDeleteObj] = useState({})
    const buttonRef = useRef();
    const classes = useStyles();

    const handleEditDashboardFilter = (form) => {
        if (form) {
            let sheetFilter = structuredClone(currentSheet.sheetFilter);
            sheetFilter[form?.index].applicableTo = form?.applicableTo;
            sheetFilter[form?.index].applyTo = form?.applyTo;
            sheetFilter[form?.index].displayNameOnSheet = form?.displayNameOnSheet;
            sheetFilter[form?.index].displayType = form?.displayType;
            updateSheetFilter({ sheetFilter, sheetIndex: dashboardState.currentSheetIndex });
        }
    }

    const addFilter = (event) => {
        const buttonWidth = buttonRef.current.clientWidth;
        setFilterButtonWidth(buttonWidth);
        setAnchorAddFilter(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorAddFilter(null);
    };

    const deleteAllFilterPopUp = () => {
        setDeleteAllPopup(true)
    }


    const deleteReportFilterPopUp = (filter, index, valueList) => {
        setDeleteObj({ filter, index, valueList });
        setDeletePopup(true)
    }

    const deleteReportFilter = () => {
        let { filter, index, valueList } = deleteObj;
        const sheetFilter = structuredClone(currentSheet.sheetFilter);
        sheetFilter.splice(index, 1);
        updateSheetFilter({ sheetFilter, sheetIndex: dashboardState.currentSheetIndex });

        setDeletePopup(false)
        setDeleteObj({})
    }

    const deleteAllFilter = () => {
        updateSheetFilter({ sheetFilter: [], sheetIndex: dashboardState.currentSheetIndex });
        setDeleteAllPopup(false)
    }

    const clickMeasureListHandler = (measure, report) => {
        const fieldType = measure?.type;
        if (fieldType === 'time') {
            const isGlobalFilterApplied = currentSheet?.globalFilter?.dateRangeType;
            if (isGlobalFilterApplied) {
                toast.dismiss();
                toast.error("Global date filter is already applied. Please remove it before adding a new date filter.");
                return;
            }
        }
        measure.reportId = report
        measure.reportObjId = report?._id
        const clonedSheet = structuredClone(currentSheet);
        clonedSheet.sheetFilter.push(measure);
        updateSheet({ sheet: clonedSheet, sheetIndex: dashboardState.currentSheetIndex });
        setAnchorAddFilter(null);
    }

    const addOrEdit = (form, resetForm) => {
        resetForm()
        setOpenPopup(false);
        handleEditDashboardFilter(form);
    }

    const openInPopup = (obj, i) => {
        setRecordForEdit({ ...obj, index: i })
        setOpenPopup(true)
    }

    const closeEditFilterModal = () => {
        setRecordForEdit({})
        setOpenPopup(false)
    }

    const filterFormStateChange = (type, index, value) => {

        if (type === "filterOperation") {
            filterFormState[index].operator = value;
        }
        let filterFormStateNew = [...filterFormState];
        setFilterFormState(filterFormStateNew);
    }

    const handleCancelDeleteAll = () => {
        setDeleteAllPopup(false)
    }

    const handleCancelDelete = () => {
        setDeletePopup(false)
    }

    const generateFilterList = () => {
        let filterList = []
        if (filterOptionList?.length) {
            filterList = filterOptionList?.map((reportObj, i) => {
                if (reportObj?.filterOptionList?.length) {
                    return reportObj?.filterOptionList.map(measure => (
                        <ListItem className={classes.searchFieldListItem} key={`report-filter-${i}-${reportObj.report._id}`} button onClick={() => clickMeasureListHandler(measure, reportObj?.report)}
                            alignItems="flex-start">
                            <ListItemIcon sx={{ mt: '0px', mr: '4px' }} className={classes.searchFieldListIcon}>
                                {measure?.dataSetType === "MEASURE" ? <FunctionsOutlinedIcon className="cm_dimension_icon icon_color_main" /> : <LabelOutlinedIcon className="cm_measr_lbl icon_color_main" />}
                            </ListItemIcon>
                            <Grid container>
                                <Grid xs={12}>
                                    <div className="filterlist-text">
                                        <Typography className="text-plain"> {(measure?.member ? reportObj?.report?.reportName + ' - ' + (measure?.displayName ? measure?.member.split('.')[0] + '.' + measure?.displayName : measure?.member) : (measure?.displayName || measure?.name))} </Typography>
                                        <span>{(measure?.useRelevant?.length || measure?.relevantTo) ? <CopyrightIcon /> : ''}</span>
                                    </div>
                                </Grid>
                                <Grid xs={12}>
                                    <Typography variant="subtitle2" className={classes.listItemDescription + " text-sub"}>{measure?.description} </Typography>
                                </Grid>
                            </Grid>
                        </ListItem>
                    ))
                } else {
                    return null
                }
            })
        }

        if (filterList.length) {
            return filterList.filter(filter => filter !== undefined && filter !== '')
        }
        else {
            return filterList
        }

    }

    return (
        <RootStyle>
            <Box className="report-filters-section">
                <Typography variant="h5" className="filter-heading" >Add Filter</Typography>
                <Box height="100%" py={2} >
                    <Button className="width-100 report_form_ui_btn primary" variant="contained" color="primary" startIcon={<AddIcon />} onClick={addFilter}
                        ref={buttonRef} >
                        Add Filter Condition
                    </Button>
                    <Popover
                        id="measure-list"
                        open={Boolean(anchorAddFilter)}
                        anchorEl={anchorAddFilter}
                        onClose={handleClose}
                        PaperProps={{
                            style: {
                                width: filterButtonWidth, height: 'fit-content', maxHeight: '300px', borderTopLeftRadius: 0,
                                borderTopRightRadius: 0, marginTop: '10px', backgroundColor: 'transparent'
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <Box className="width-100">
                            <Box className="field_list report_filter_list height-fit max-width-100 mt-0" sx={{ maxHeight: '300px', backgroundColor: '#fff' }} >
                                <List component="nav" aria-label="main mailbox folders" className="field_list">
                                    {
                                        generateFilterList().length ? generateFilterList() :
                                            <ListItem button >
                                                No filters found!
                                            </ListItem>
                                    }
                                </List>
                            </Box>
                        </Box>

                    </Popover>
                    <Box>
                        <List component="nav" aria-label="main mailbox folders" className="field_list" sx={{ p: 0, mt: 1 }}>
                            {currentSheet?.sheetFilter?.map((obj, i) => (
                                <ListItem key={`sheet-filter-${i}`} sx={{ p: 0 }}>
                                    <Box display="block" width="100%" className="filter-box-item" >
                                        <Box>
                                            <Stack direction="row" width="100%" spacing={1} flexShrink={0} sx={{ my: 0 }}>
                                                <ListItemIcon sx={{ paddingTop: '5px', marginRight: '12px' }}>
                                                    <HashtagIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    sx={{ marginLeft: '0px !important' }}
                                                    className="box-col-pointer text-plain"
                                                    primary={(obj?.reportId?.reportName && (obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1]) && obj?.member ? `${obj?.reportId?.reportName} - ${(obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1])} - ${(obj?.displayName ? obj?.member.split('.')[0] + '.' + obj?.displayName : obj?.member)}` : !obj?.reportId?.reportName && (obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1]) && obj?.member ? `${(obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1])} - ${obj?.member}` : obj?.reportId?.reportName && !(obj?.displayNameOnSheet || obj?.member?.split('.')[1]) && obj?.member ? `${obj?.reportId?.reportName} - ${(obj?.displayName ? obj?.member.split('.')[0] + '.' + obj?.displayName : obj?.member)}` : obj?.reportId?.reportName && (obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1]) && !obj?.member ? `${obj?.reportId?.reportName} - ${(obj?.displayNameOnSheet || obj?.displayName || obj?.member?.split('.')[1])}` : (obj?.displayName ? obj?.member.split('.')[0] + '.' + obj?.displayName : obj?.member))}
                                                    secondary={obj?.operator}
                                                />
                                                {(obj?.useRelevant?.length || obj?.relevantTo ? <CopyrightIcon /> : '')}

                                            </Stack>
                                            <Stack direction="row" sx={{ mt: 2, pt: 1 }}>
                                                <CustomTooltip title="Edit filter">
                                                    <IconButton color="primary" aria-label="Edit filter" component="span" className="hover-white form-icon-btn"
                                                        sx={{ p: 0, marginRight: 4 }}
                                                        onClick={() => openInPopup(obj, i)}>
                                                        <EditIcon className="filter-action-icon" />
                                                        <Typography variant="subtitle2" className="text-small" sx={{ pl: 1 }}>Edit</Typography>
                                                    </IconButton>
                                                </CustomTooltip>
                                                <CustomTooltip title="Delete filter">
                                                    <IconButton color="primary" aria-label="Delete filter" component="span" className="hover-white form-icon-btn"
                                                        sx={{ p: 0, marginRight: 4 }}
                                                        onClick={() => deleteReportFilterPopUp(obj, i, valueList)} >
                                                        <DeleteIcon className="filter-action-icon" />
                                                        <Typography variant="subtitle2" className="text-small" sx={{ pl: 1 }}>Delete</Typography>
                                                    </IconButton>
                                                </CustomTooltip>

                                            </Stack>
                                        </Box>
                                        <Box style={{ display: filterFormOpen === i ? "block" : "none" }}>
                                            <form id={"filter" + i}>
                                                {obj && <ReportDashboardFilterForm reportDashboardFilter={obj} i={i} filterFormStateChange={filterFormStateChange} />}
                                            </form>
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                    {
                        currentSheet?.sheetFilter?.length ? (<div>
                            <Stack direction="row" width="100%" spacing={1} flexShrink={0} sx={{ my: 1 }}>
                                {/* <Button width="100%" className="report_form_ui_btn primary text-btn-small mr-2" variant="contained" color="primary" onClick={applyFilter} >
                                    Apply Filter
                                </Button> */}
                                <Button width="100%" className="report_form_ui_btn primary text-btn-small" variant="contained" color="primary" onClick={deleteAllFilterPopUp}>
                                    Delete all
                                </Button>
                            </Stack>
                        </div>) : (<></>)
                    }
                </Box>

                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    className="modal-root"
                    open={openPopup}
                    onClose={() => setOpenPopup(false)}
                    closeAfterTransition
                >
                    <Fade in={openPopup}>
                        <Box className="modal-paper modal-box modal-md" id="transition-modal-title">
                            <CloseIcon onClick={closeEditFilterModal} className="enlarge-close" />
                            <Box className="modal-header" >
                                <Typography variant="subtitle1" className="modal-header-title" >{`Edit Filter`}</Typography>

                            </Box>
                            <Box className="modal-content">
                                <FormatControl justifyContent="flex-start" addOrEdit={addOrEdit} recordForEdit={recordForEdit} />
                            </Box>

                        </Box>
                    </Fade>
                </Modal>
                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    className="modal-root"
                    open={deletePopup}
                    onClose={() => setDeletePopup(false)}
                    closeAfterTransition
                >
                    <Fade in={deletePopup}>
                        <Box className="modal-paper modal-box modal-sm" id="transition-modal-title">
                            <Box className="modal-content-simple">
                                <Typography variant="subtitle1" className="modal-heading-title-simple">{`Are you sure to delete this Filter ?`}</Typography>
                                <Box className="modal-footer-simple">
                                    <Button onClick={handleCancelDelete} color="primary" className="report_form_ui_btn cancel mr-2" variant="outlined" > Cancel </Button>
                                    <Button onClick={deleteReportFilter} color="error" autoFocus className="report_form_ui_btn submit" variant="contained"> Delete </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Modal>

                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    className="modal-root"
                    open={deleteAllPopup}
                    onClose={() => setDeleteAllPopup(false)}
                    closeAfterTransition
                >
                    <Fade in={deleteAllPopup}>
                        <Box className="modal-paper modal-box modal-sm" id="transition-modal-title">
                            <Box className="modal-content-simple">
                                <Typography variant="subtitle1" className="modal-heading-title-simple">{`Are you sure to delete all Filters ?`}</Typography>
                                <Box className="modal-footer-simple">
                                    <Button onClick={handleCancelDeleteAll} color="primary" className="report_form_ui_btn cancel mr-2" variant="outlined" > Cancel </Button>
                                    <Button onClick={deleteAllFilter} color="error" autoFocus className="report_form_ui_btn submit" variant="contained"> Delete </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Modal>
            </Box>

            <Box className="report-filters-section">
                <GlobalDateRange
                    currentSheet={currentSheet}
                    selectedSheetIndex={dashboardState.currentSheetIndex}
                    updateSheet={updateSheet}
                />
            </Box>
            <Box className="report-filters-section">
                <DashboardGranularity
                    granularityOptions={granularityOptions}
                    addGranularityToSheet={addGranularityToSheet}
                    sheetsAvailable={sheetsAvailable}
                    setSheetsAvailable={setSheetsAvailable}
                    selectedSheetIndex={selectedSheetIndex}
                    applyGranularityToAllReports={applyGranularityToAllReports}
                    removeGranularityFrmAllReports={removeGranularityFrmAllReports}
                />
            </Box>
        </RootStyle>
    );
}
